import {
  XCircle,
  CheckCircle2,
  AlertTriangle,
  TrendingDown,
  Rocket,
  Zap,
} from 'lucide-react'
const ProblemSolution = () => {
  return (
    <section className="py-24 relative bg-[#012622] bg-tactical-dots">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-xs uppercase font-mono tracking-widest text-[#00FF9D] font-bold px-3 py-1 rounded-full bg-[#05332E] border border-[#00FF9D]/20 inline-block mb-3">
            El Techo de Cristal de las Agencias
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white tracking-tight mb-4">
            El modelo tradicional vs.{' '}
            <span className="text-[#00FF9D]">La ventaja Hutrit</span>
          </h2>
          <p className="text-base sm:text-lg text-[#CBD5E1]">
            ¿Por qué la mayoría de agencias se estancan al alcanzar los 10
            clientes? Porque su estructura de costes escala en línea recta con
            su volumen de trabajo.
          </p>
        </div>

        {/* Dos columnas contrastadas */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch max-w-5xl mx-auto">
          {/* Columna 1: El modelo tradicional */}
          <div className="rounded-2xl p-7 sm:p-9 bg-[#031E1B]/90 border border-red-500/20 shadow-xl flex flex-col justify-between relative overflow-hidden group hover:border-red-500/40 transition-colors">
            <div className="absolute -top-12 -right-12 w-36 h-36 bg-red-500/5 rounded-full blur-2xl pointer-events-none" />

            <div>
              {/* Header */}
              <div className="flex items-center justify-between pb-6 mb-6 border-b border-white/10">
                <div>
                  <span className="text-xs font-mono font-bold uppercase tracking-wider text-red-400 flex items-center gap-1.5 mb-1">
                    <TrendingDown className="w-4 h-4" />
                    El modelo tradicional
                  </span>
                  <h3 className="text-2xl font-bold text-white">
                    Límite de crecimiento
                  </h3>
                </div>
                <div className="w-12 h-12 rounded-xl bg-red-500/10 border border-red-500/20 flex items-center justify-center text-red-400">
                  <AlertTriangle className="w-6 h-6" />
                </div>
              </div>

              {/* Problem Items */}
              <div className="space-y-6">
                {/* Cuellos de botella */}
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-lg bg-red-500/10 text-red-400 flex-shrink-0 flex items-center justify-center mt-1 border border-red-500/20">
                    <XCircle className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="text-base font-semibold text-white mb-1">
                      Cuellos de botella continuos
                    </h4>
                    <p className="text-sm text-[#CBD5E1] leading-relaxed">
                      Atender a más de 10 clientes exige contratar más
                      plantilla, disparando la nómina mensual y multiplicando
                      las horas de onboarding.
                    </p>
                  </div>
                </div>

                {/* Margen menguante */}
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-lg bg-red-500/10 text-red-400 flex-shrink-0 flex items-center justify-center mt-1 border border-red-500/20">
                    <XCircle className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="text-base font-semibold text-white mb-1">
                      Margen de beneficio menguante
                    </h4>
                    <p className="text-sm text-[#CBD5E1] leading-relaxed">
                      A mayor número de clientes, más horas operativas se
                      consumen en tareas repetitivas. Trabajas el doble para
                      ganar prácticamente lo mismo.
                    </p>
                  </div>
                </div>

                {/* Inconsistencia */}
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-lg bg-red-500/10 text-red-400 flex-shrink-0 flex items-center justify-center mt-1 border border-red-500/20">
                    <XCircle className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="text-base font-semibold text-white mb-1">
                      Inconsistencia en las entregas
                    </h4>
                    <p className="text-sm text-[#CBD5E1] leading-relaxed">
                      La rotación de personal, bajas imprevistas o la sobrecarga
                      de trabajo afectan drásticamente la velocidad y calidad
                      final de los entregables.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom summary box */}
            <div className="mt-8 pt-4 border-t border-white/10 flex items-center justify-between text-xs font-mono text-red-300 bg-red-950/20 p-3 rounded-xl border border-red-500/20">
              <span>Coste por cliente tradicional:</span>
              <span className="font-bold text-white text-sm">
                ~600 € - 1.200 € / mes
              </span>
            </div>
          </div>

          {/* Columna 2: El modelo Hutrit [Highlight Verde] */}
          <div className="rounded-2xl p-7 sm:p-9 bg-[#05332E] border-2 border-[#00FF9D] shadow-[0_0_40px_rgba(0,255,157,0.18)] flex flex-col justify-between relative overflow-hidden">
            {/* Top pill indicator */}
            <div className="absolute top-0 right-0 bg-[#00FF9D] text-[#012622] text-[11px] font-extrabold uppercase tracking-wider px-4 py-1 rounded-bl-xl shadow-md">
              Arquitectura Recomendada
            </div>

            <div className="absolute -top-12 -right-12 w-48 h-48 bg-[#00FF9D]/10 rounded-full blur-2xl pointer-events-none" />

            <div>
              {/* Header */}
              <div className="flex items-center justify-between pb-6 mb-6 border-b border-[#00FF9D]/20">
                <div>
                  <span className="text-xs font-mono font-bold uppercase tracking-wider text-[#00FF9D] flex items-center gap-1.5 mb-1">
                    <Zap className="w-4 h-4 fill-[#00FF9D]" />
                    El modelo Hutrit
                  </span>
                  <h3 className="text-2xl font-bold text-white">
                    Escalabilidad infinita
                  </h3>
                </div>
                <div className="w-12 h-12 rounded-xl bg-[#00FF9D]/20 border border-[#00FF9D]/40 flex items-center justify-center text-[#00FF9D]">
                  <Rocket className="w-6 h-6" />
                </div>
              </div>

              {/* Solution Items */}
              <div className="space-y-6">
                {/* Cero fricción operativa */}
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-lg bg-[#00FF9D]/20 text-[#00FF9D] flex-shrink-0 flex items-center justify-center mt-1 border border-[#00FF9D]/40">
                    <CheckCircle2 className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="text-base font-semibold text-white mb-1">
                      Cero fricción operativa
                    </h4>
                    <p className="text-sm text-[#CBD5E1] leading-relaxed">
                      Añades capacidad operativa inmediata para 10 clientes sin
                      costes de contratación ni periodos de aprendizaje: tu
                      equipo de IA ya es experto.
                    </p>
                  </div>
                </div>

                {/* Margen Ultra-Alto */}
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-lg bg-[#00FF9D]/20 text-[#00FF9D] flex-shrink-0 flex items-center justify-center mt-1 border border-[#00FF9D]/40">
                    <CheckCircle2 className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="text-base font-semibold text-white mb-1">
                      Margen Ultra-Alto
                    </h4>
                    <p className="text-sm text-[#CBD5E1] leading-relaxed">
                      Fijas tu coste operativo en solo{' '}
                      <strong className="text-[#00FF9D]">
                        50 €/mes por cliente
                      </strong>{' '}
                      y disparas tu margen de beneficio neto en cada cuenta
                      nueva.
                    </p>
                  </div>
                </div>

                {/* Atención Ultra-Premium 24/7 */}
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-lg bg-[#00FF9D]/20 text-[#00FF9D] flex-shrink-0 flex items-center justify-center mt-1 border border-[#00FF9D]/40">
                    <CheckCircle2 className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="text-base font-semibold text-white mb-1">
                      Atención Ultra-Premium 24/7
                    </h4>
                    <p className="text-sm text-[#CBD5E1] leading-relaxed">
                      Agentes que no duermen, no se saturan, ejecutan
                      entregables en minutos y mantienen el estándar de calidad
                      alto de forma homogénea.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom summary box */}
            <div className="mt-8 pt-4 border-t border-[#00FF9D]/20 flex items-center justify-between text-xs font-mono text-[#00FF9D] bg-[#012622] p-3 rounded-xl border border-[#00FF9D]/40 shadow-sm">
              <span className="text-white font-medium">
                Coste por cliente con Hutrit:
              </span>
              <span className="font-bold text-[#00FF9D] text-sm">
                &lt; 50 € / mes (menos de 1,70 €/día)
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
export { ProblemSolution }
